import React, { useEffect, useState } from 'react';
import { ReferralStats as ReferralStatsType } from '../../interfaces/transporters.interface';
import { authService } from '../../service/AuthService';

interface Props {
  onError?: (error: string) => void;
}

const ReferralStats: React.FC<Props> = ({ onError }) => {
  const [stats, setStats] = useState<ReferralStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [canShare, setCanShare] = useState(false);
  const [copyButtonText, setCopyButtonText] = useState('Copier le lien');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await authService.getReferralStats();
        setStats(data);
      } catch (err) {
        const errorMessage = "Erreur lors de la récupération des statistiques de parrainage";
        console.error(errorMessage, err);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    // Check if either Web Share API or clipboard API is available
    setCanShare(!!navigator.share || !!navigator.clipboard);

    fetchStats();
  }, [onError]);

  const getReferralLink = (code: string) => {
    return `${process.env.REACT_APP_URL}/signup?code=${code}`;
  };

  const handleShare = async () => {
    if (!stats) return;
    
    const shareData = {
      title: 'Rejoignez ProDrive',
      text: 'Utilisez mon code de parrainage pour vous inscrire sur ProDrive !',
      url: getReferralLink(stats.referralCode)
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(getReferralLink(stats.referralCode));
        setCopyButtonText('Copié !');
        setTimeout(() => setCopyButtonText('Copier le lien'), 2000);
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const handleCopy = async () => {
    if (!stats) return;
    try {
      await navigator.clipboard.writeText(getReferralLink(stats.referralCode));
      setCopyButtonText('Copié !');
      setTimeout(() => setCopyButtonText('Copier le lien'), 2000);
    } catch (err) {
      console.error('Error copying:', err);
      setCopyButtonText('Erreur');
      setTimeout(() => setCopyButtonText('Copier le lien'), 2000);
    }
  };

  if (loading) return <div className="animate-pulse">Chargement...</div>;
  if (!stats) return null;

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-semibold mb-6">Programme de Parrainage</h2>
      
      {/* Referral Code Section */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Votre Code de Parrainage</h3>
        <div className="flex items-center gap-2">
          <code className="bg-gray-100 px-4 py-2 rounded font-mono text-lg">
          {process.env.REACT_APP_URL}/signup?code={stats.referralCode}
          </code>
          {navigator.clipboard && (
            <button
              onClick={handleCopy}
              className="bg-primary hover:bg-primary-light text-white px-3 py-2 rounded transition-colors min-w-[120px]"
            >
              {copyButtonText}
            </button>
          )}
          {canShare && (
            <button
              onClick={handleShare}
              className="bg-primary hover:bg-primary-light text-white px-3 py-2 rounded transition-colors flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
              </svg>
              Partager
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Partagez ce lien avec d'autres transporteurs pour gagner des récompenses
        </p>
      </div>

      {/* Progress Section */}
      <div className="mb-6">
        <div className="bg-gray-100 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">Progression vers la récompense</span>
            <span className="text-sm font-medium">
              {stats.referralCount}/5 parrainages
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${(stats.referralCount / 5) * 100}%` }}
            ></div>
          </div>
          {stats.hasClaimedReward ? (
            <p className="text-green-600 text-sm mt-2">
              Félicitations ! Vous avez déjà réclamé vos 2 mois gratuits.
            </p>
          ) : stats.isEligibleForReward ? (
            <p className="text-primary text-sm mt-2">
              Félicitations ! Vous êtes éligible pour 2 mois gratuits.
            </p>
          ) : (
            <p className="text-sm text-gray-600 mt-2">
              Plus que {5 - stats.referralCount} parrainages pour obtenir 2 mois gratuits !
            </p>
          )}
        </div>
      </div>

      {/* Referred Transporters List */}
      {stats.referredTransporters.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Transporteurs Parrainés</h3>
          <div className="border rounded-lg divide-y">
            {stats.referredTransporters.map((transporter) => (
              <div key={transporter._id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">
                      {transporter.name} {transporter.lastName}
                    </p>
                    <p className="text-sm text-gray-600">{transporter.companyName}</p>
                  </div>
                  <div className="text-sm text-gray-500">{transporter.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralStats;
